import { useState } from "react";
import { spot as spotCore, type AutopilotStatus, type AutopilotView } from "@auto-pools/core";
import { useAutopilot } from "./autopilotContext";
import { useViewer } from "../auth/authContext";
import { AutopilotStartDialog } from "./AutopilotStartDialog";
import { AutopilotPauseDialog } from "./AutopilotPauseDialog";

type Tone = "neutral" | "good" | "warn" | "bad";

/**
 * Red only while money is exposed AND the run can no longer manage it on its own (design 47,
 * section 1): a stuck close or an unproven opening under `needs-attention`. A spent daily limit
 * and a locked vault are expected stops, so they stay amber; a healthy run in position is green.
 */
function pillTone(status: AutopilotStatus): Tone {
  if (status.hold) return "warn";
  switch (status.state) {
    case "off":
    case "paused":
      return "neutral";
    case "stopping":
    case "limit":
      return "warn";
    case "needs-attention":
      return status.cycles.length > 0 ? "bad" : "warn";
    case "running":
      return "good";
  }
}

function pillLabel(status: AutopilotStatus): string {
  if (status.hold === "vault-locked") return "Autopilot · vault locked";
  if (status.hold === "recovering") return "Autopilot · recovering";
  switch (status.state) {
    case "off":
      return "Autopilot off";
    case "paused":
      return "Autopilot paused";
    case "stopping":
      return "Stopping…";
    case "limit": {
      const limit = status.reason?.state === "limit" ? status.reason.limit : null;
      return limit === "entries" ? "Stopped · entry limit" : "Stopped · drawdown";
    }
    case "needs-attention":
      return "Autopilot needs attention";
    case "running": {
      const { cycles } = status;
      if (cycles.length === 0) return "Autopilot running";
      if (cycles.length > 1) return `Autopilot · ${String(cycles.length)} positions`;
      const cycle = cycles[0];
      if (cycle.phase === "open") return "Autopilot · in position";
      if (cycle.phase === "opening") return "Autopilot · opening";
      return "Autopilot · closing";
    }
  }
}

/** What a running pools engine is doing, for the two-engine label ("2 pools", "opening"). */
function poolsActivity(status: AutopilotStatus): string | null {
  const { cycles } = status;
  if (cycles.length === 0) return null;
  if (cycles.length > 1) return `${String(cycles.length)} pools`;
  const phase = cycles[0].phase;
  return phase === "open" ? "1 pool" : phase === "opening" ? "opening" : "closing";
}

/**
 * The pill's words over both engines (design 54: one pill shows the worse of the two; design 50
 * "Autopilot · 2 pools · spot"). Without an active spot leg — no `spot`, or spot off or paused
 * with nothing wrong — it is exactly the pools label. The red comes from `controls.tone`, the
 * server's combined tone; it is spot's red when the pools tone (core `poolsTone`) is not red.
 * Pools holds, pools needing attention and pools stopping keep their own wording first.
 */
function combinedPillLabel(view: AutopilotView): string {
  const { status, spot, controls } = view;
  const poolsLabel = pillLabel(status);
  if (!spot || !controls) return poolsLabel;
  const spotActive =
    spot.lifecycle === "starting" || spot.lifecycle === "running" || spot.lifecycle === "stopping";
  if (!spotActive && spot.state !== "needs-attention") return poolsLabel;
  // The red spot risk outranks an amber pools hold; any other hold keeps its own wording.
  if (controls.tone === "bad" && spotCore.poolsTone(status) !== "bad")
    return "Autopilot · money at risk";
  if (status.hold) return poolsLabel;
  if (status.state === "needs-attention" || status.state === "stopping") return poolsLabel;
  if (spot.state === "needs-attention") return "Autopilot · spot needs attention";
  if (spot.hold !== null) return "Autopilot · spot not trading";
  if (spot.lifecycle === "stopping") return status.state === "running" ? poolsLabel : "Stopping…";
  switch (status.state) {
    case "running": {
      const activity = poolsActivity(status);
      return activity === null ? "Autopilot · running" : `Autopilot · ${activity} · spot`;
    }
    case "paused":
      return "Autopilot · spot · pools paused";
    case "limit":
      return "Autopilot · spot · pools stopped";
    case "off":
      return "Autopilot · spot · pools off";
  }
}

type PillAction =
  | { kind: "start"; label: "Start" }
  | { kind: "pause"; label: "Pause" }
  | { kind: "open"; label: "Open" }
  | { kind: "none"; label: "Stopping…" };

function pillAction(status: AutopilotStatus): PillAction {
  switch (status.state) {
    case "off":
      return { kind: "start", label: "Start" };
    case "running":
      return { kind: "pause", label: "Pause" };
    case "stopping":
      return { kind: "none", label: "Stopping…" };
    case "paused":
    case "limit":
    case "needs-attention":
      return { kind: "open", label: "Open" };
  }
}

/**
 * The combined action (design 54) from the server's `controls` (`packages/core/src/spot/controls.ts`
 * `autopilotControls`), never re-derived here: Pause while either engine is pausable, Start while
 * the pools autopilot is off, Stopping… while something stops and nothing else applies, otherwise
 * Open — Resume and the attention details live on the Autopilot page (design 47). Pools in `limit`
 * or `needs-attention` keep routing to the page first, as before, so the operator sees why. Older
 * payloads without `controls` keep the exact pools-only mapping above.
 */
function combinedPillAction(view: AutopilotView): PillAction {
  const { controls } = view;
  if (!controls) return pillAction(view.status);
  const poolsAttention = view.status.state === "limit" || view.status.state === "needs-attention";
  if (controls.pause && !poolsAttention) return { kind: "pause", label: "Pause" };
  if (controls.start) return { kind: "start", label: "Start" };
  if (controls.stopping) return { kind: "none", label: "Stopping…" };
  return { kind: "open", label: "Open" };
}

/**
 * The Start/Pause/Open button plus its dialogs, shared by the pill (desktop and the open mobile
 * sheet) and the ≤760 px status strip, which gives the same action a second, larger home once the
 * header has no room for it (design 47, section 10). `className` lets each caller keep its own
 * button chrome while the action logic and dialogs stay in one place.
 */
export function AutopilotActionButton({
  view,
  onOpen,
  className,
}: {
  view: AutopilotView;
  onOpen: () => void;
  className: string;
}) {
  const autopilot = useAutopilot();
  const viewer = useViewer();
  const [dialog, setDialog] = useState<"start" | "pause" | null>(null);
  const action = combinedPillAction(view);
  // Start and Pause are commands: a viewer keeps Open (navigation) and the disabled Stopping….
  if (viewer && (action.kind === "start" || action.kind === "pause")) return null;
  return (
    <>
      <button
        type="button"
        className={className}
        disabled={action.kind === "none"}
        onClick={() => {
          if (action.kind === "start") setDialog("start");
          else if (action.kind === "pause") setDialog("pause");
          else if (action.kind === "open") onOpen();
        }}
      >
        {action.label}
      </button>
      {dialog === "start" && (
        <AutopilotStartDialog
          view={view}
          onClose={() => {
            setDialog(null);
          }}
          onStarted={() => {
            setDialog(null);
            autopilot.reload();
          }}
        />
      )}
      {dialog === "pause" && (
        <AutopilotPauseDialog
          view={view}
          onClose={() => {
            setDialog(null);
          }}
          onPaused={() => {
            setDialog(null);
            autopilot.reload();
          }}
        />
      )}
    </>
  );
}

export function AutopilotPill({ onOpen }: { onOpen: () => void }) {
  const autopilot = useAutopilot();
  const view: AutopilotView | null = autopilot.data;
  if (!view) {
    return (
      <span className="autopilot-pill neutral" aria-label="Autopilot status loading">
        <span className="status-dot neutral" />
        <span>Autopilot</span>
      </span>
    );
  }
  const tone = view.controls?.tone ?? pillTone(view.status);
  const label = combinedPillLabel(view);
  return (
    <div className="autopilot-pill-control">
      <span className={`autopilot-pill ${tone}`}>
        <span className={`status-dot ${tone}`} />
        <span>{label}</span>
        <AutopilotActionButton view={view} onOpen={onOpen} className="autopilot-pill-action" />
      </span>
    </div>
  );
}
